import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { TrendingUp, TrendingDown, Minus, Target, ShieldAlert, LogIn, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { journalAPI } from '../../services/api';

const BIAS_STYLES = {
  bullish: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: TrendingUp },
  bearish: { badge: 'bg-red-100 text-red-700 border-red-200', Icon: TrendingDown },
  neutral: { badge: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Minus },
};

const LevelBox = ({ label, value, icon: Icon, tone, testId }) => (
  <div className={`rounded-xl border p-4 text-center ${tone}`} data-testid={testId}>
    <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider opacity-70 mb-1">
      <Icon size={12} />
      {label}
    </div>
    <p className="text-xl font-bold">{value != null ? `$${value}` : '—'}</p>
  </div>
);

const parseRiskReward = (rr) => {
  // LLM returns e.g. "1:2.36" — journal expects a numeric ratio (2.36)
  if (rr == null) return null;
  if (typeof rr === 'number') return rr;
  const last = String(rr).split(':').pop();
  const n = parseFloat(last);
  return Number.isFinite(n) ? n : null;
};

const AnalysisResultCard = ({ result }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const a = result.analysis;
  const { badge, Icon } = BIAS_STYLES[a.bias] || BIAS_STYLES.neutral;
  const canJournal = a.symbol && a.entry != null;

  const saveToJournal = async () => {
    setSaving(true);
    try {
      await journalAPI.create({
        symbol: a.symbol,
        side: a.bias === 'bearish' ? 'short' : 'buy',
        size: 1,
        entryPrice: a.entry,
        stopLoss: a.stop_loss,
        takeProfit: a.take_profit,
        strategy: 'AI Chart Analyzer',
        setup: (a.patterns || []).join(', ') || 'AI chart analysis',
        thesis: a.rationale,
        riskRewardRatio: parseRiskReward(a.risk_reward),
        confidence: a.confidence,
        tags: ['ai-chart-analyzer'],
        status: 'open',
      });
      setSaved(true);
      toast.success('Trade idea saved to your journal');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save to journal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-purple-100" data-testid="chart-analysis-result">
      <CardContent className="p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge className={`${badge} border text-sm px-3 py-1`} data-testid="analysis-bias-badge">
              <Icon size={14} className="mr-1" />
              {a.bias.toUpperCase()}
            </Badge>
            {a.symbol && <span className="text-lg font-bold text-gray-900">{a.symbol}</span>}
            {a.timeframe && <span className="text-xs text-gray-500">{a.timeframe}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Confidence</span>
            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-cyan-500"
                style={{ width: `${a.confidence}%` }}
              />
            </div>
            <span className="text-sm font-semibold text-gray-800">{a.confidence}%</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <LevelBox label="Entry" value={a.entry} icon={LogIn} testId="analysis-entry"
            tone="border-blue-200 bg-blue-50 text-blue-800" />
          <LevelBox label="Take Profit" value={a.take_profit} icon={Target} testId="analysis-take-profit"
            tone="border-emerald-200 bg-emerald-50 text-emerald-800" />
          <LevelBox label="Stop Loss" value={a.stop_loss} icon={ShieldAlert} testId="analysis-stop-loss"
            tone="border-red-200 bg-red-50 text-red-800" />
        </div>

        {a.risk_reward && (
          <p className="text-sm text-gray-600">
            Risk / Reward: <span className="font-semibold text-gray-900">{a.risk_reward}</span>
          </p>
        )}

        {a.patterns?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="analysis-patterns">
            {a.patterns.map((p) => (
              <Badge key={p} variant="outline" className="text-xs text-purple-700 border-purple-200 bg-purple-50">
                {p}
              </Badge>
            ))}
          </div>
        )}

        {a.technical_summary && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Technical</p>
            <p className="text-sm text-gray-700">{a.technical_summary}</p>
          </div>
        )}
        {a.fundamental_summary && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Fundamental</p>
            <p className="text-sm text-gray-700">{a.fundamental_summary}</p>
          </div>
        )}
        {a.rationale && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Trade Rationale</p>
            <p className="text-sm text-gray-700 whitespace-pre-line" data-testid="analysis-rationale">{a.rationale}</p>
          </div>
        )}

        {canJournal && (
          <Button
            onClick={saveToJournal}
            disabled={saving || saved}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="analysis-save-journal-btn"
          >
            <BookOpen size={15} className="mr-1.5" />
            {saved ? 'Saved to Journal ✓' : saving ? 'Saving…' : 'Save to Journal'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalysisResultCard;
